/** Decoded JWT payload for DMARC Guardian access tokens. */
export interface AccessTokenPayload {
  exp: number
  tkv: number
  uid: string
  oid: string
  role: string
  /**
   * When present and set to "password_change_only", the token is a scoped
   * token issued after login when the user must change their password before
   * accessing any other resource.
   */
  scope?: string
  /**
   * When present, contains the email of the admin who initiated impersonation.
   * Used by ImpersonationBanner to display the impersonator's identity.
   */
  imp_by?: string
}

/**
 * Decodes JWT payload WITHOUT signature verification.
 * Never use for authorization decisions — only for reading client-side
 * expiry/schedule hints. The backend always validates the signature.
 *
 * @returns Parsed payload or null if the token is malformed.
 */
export function decodeAccessToken(token: string): AccessTokenPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    // base64url → base64 → JSON
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const payload = JSON.parse(json)

    if (
      typeof payload.exp !== 'number' ||
      typeof payload.uid !== 'string' ||
      typeof payload.role !== 'string'
    ) {
      return null
    }

    return {
      exp: payload.exp,
      tkv: typeof payload.tkv === 'number' ? payload.tkv : 0,
      uid: payload.uid,
      oid: typeof payload.oid === 'string' ? payload.oid : '',
      role: payload.role,
      scope: typeof payload.scope === 'string' ? payload.scope : undefined,
      imp_by: typeof payload.imp_by === 'string' ? payload.imp_by : undefined,
    }
  } catch {
    return null
  }
}

/**
 * Returns true if the token is expired (exp <= now).
 * Returns true for malformed tokens to fail safe.
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeAccessToken(token)
  if (!payload) return true
  return payload.exp <= Math.floor(Date.now() / 1000)
}

/**
 * Returns true if the token will expire within `thresholdMs` milliseconds.
 * Returns true for malformed tokens to fail safe.
 */
export function isTokenExpiringSoon(
  token: string,
  thresholdMs = 60_000
): boolean {
  const payload = decodeAccessToken(token)
  if (!payload) return true
  const expiresInMs = payload.exp * 1000 - Date.now()
  return expiresInMs <= thresholdMs
}

/**
 * Returns milliseconds until the token expires.
 * Returns 0 for expired or malformed tokens.
 */
export function getTokenExpiresIn(token: string): number {
  const payload = decodeAccessToken(token)
  if (!payload) return 0
  return Math.max(0, payload.exp * 1000 - Date.now())
}

/**
 * Schedules a proactive token refresh at 80% of the token's remaining TTL.
 * Returns the setTimeout handle so the caller can cancel it (clearTimeout).
 *
 * @param token     Current access token.
 * @param refreshFn Async function that performs the refresh.
 * @returns setTimeout handle (number).
 */
export function scheduleTokenRefresh(
  token: string,
  refreshFn: () => Promise<void>
): ReturnType<typeof setTimeout> {
  const expiresInMs = getTokenExpiresIn(token)
  // Refresh at 80% of remaining TTL, minimum 5 seconds from now.
  const delayMs = Math.max(5_000, expiresInMs * 0.8)
  return setTimeout(() => {
    refreshFn().catch((err) => {
      console.error('scheduleTokenRefresh: refresh failed', err)
    })
  }, delayMs)
}
